import { supabase } from './supabaseClient';

/**
 * 配布コードからassignmentを取得。期限切れは { expired: true } を返す。
 */
export async function fetchAssignment(code) {
  const { data, error } = await supabase
    .from('assignments')
    .select('id, title, body, expires_at')
    .eq('id', code)
    .maybeSingle(); // 存在しないコードは406ではなくnullで返す
  if (error || !data) return null;
  if (data.expires_at && new Date(data.expires_at) < new Date()) {
    return { expired: true };
  }
  return data;
}

/**
 * 生徒の提出をsubmissionsに保存（ログイン不要・匿名insert）
 *
 * detail には文字起こし・採点コメントを入れる。先生ページの「詳細」で使う。
 * ⚠️ 匿名insertなので中身はクライアント申告。点数と同じく改ざんは可能だが、
 *    コストの天井は assignment_usage（サーバーが書く）で別途担保している。
 */
export async function submitAssignment(assignmentId, studentName, type, score, audioPath = null, detail = {}) {
  // audio_path はサーバーが保存した録音の場所。本番のみ入る
  const base = {
    assignment_id: assignmentId,
    student_name: studentName,
    type,
    score,
    audio_path: audioPath,
  };

  const { error } = await supabase.from('submissions').insert({
    ...base,
    transcript:  detail.transcript || null,
    ai_feedback: detail.feedback   || null,
    ai_details:  detail.details    || null,
  });
  if (!error) return;

  // ⚠️ 詳細用の3列がまだ無い環境では insert ごと失敗し、提出が丸ごと消える。
  //    列の有無より「提出が先生に届くこと」を優先して、詳細なしで入れ直す。
  if (/transcript|ai_feedback|ai_details/.test(error.message || '')) {
    const { error: retryErr } = await supabase.from('submissions').insert(base);
    if (retryErr) console.warn('提出の保存に失敗:', retryErr);
    else console.warn('submissions に詳細用の列がないため、点数のみ保存しました');
    return;
  }
  console.warn('提出の保存に失敗:', error);
}

/**
 * 提出された録音の再生URL（1時間有効）。
 *
 * ⚠️ クライアントから直接Storageに署名付きURLをリクエストしない。
 * Storageの家庭用RLSは children.family_id 経由に絞ってあり、
 * assignments/配下は先生本人かどうかをサーバー側（/api/submission-audio-url）で
 * 確認してからでないと発行できない設計にしてある（2026-08-18・アクセス制御の穴を修正）。
 */
export async function getSubmissionAudioUrl(audioPath) {
  if (!audioPath) return null;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  const res = await fetch('/api/submission-audio-url', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ audioPath }),
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return data?.url ?? null;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * 配布課題用の文字起こし。
 * 通常の /api/transcribe ではなく、課題コード単位の上限チェックが入る
 * /api/assignment-transcribe を使う（ログイン不要のフローなのでコストの
 * 天井はサーバー側で担保する必要がある）。
 */
export async function transcribeForAssignment(audioBlob, code, mode, prompt = '') {
  const base64 = await blobToBase64(audioBlob);

  const res = await fetch('/api/assignment-transcribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      audio: base64,
      mimeType: audioBlob.type,
      prompt: prompt.slice(0, 150),
      code,
      mode,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error || `Transcribe error: ${res.status}`);
    e.status = res.status;
    throw e;
  }

  const data = await res.json();
  return {
    // Whisperが認識できなかった部分の U+FFFD（◆）や記号を除去
    transcript: (data.text?.trim() ?? '').replace(/[�■-◿]/g, ''),
    // ほんばんはClaude採点の結果が入る。失敗時は ai:false でローカル採点にフォールバック
    ai: data.ai === true ? { score: data.score, stars: data.stars, feedback: data.feedback, details: data.details } : null,
    // サーバーが保存した録音の場所（本番のみ）
    audioPath: data.audioPath ?? null,
  };
}

// ── ログイン中の子と課題の紐づけ ─────────────────────
// 配布リンクを1度開くと、その子のプロフィールに課題コードを覚える。
// 以降はアプリの「ほんをえらぼう」最上部に出るので、リンクを探さなくてよい。

/** 課題コードを子どもに紐づける（すでにあれば何もしない） */
export async function linkAssignmentToChild(childId, code) {
  if (!childId || !code) return;
  try {
    const { data, error } = await supabase
      .from('children')
      .select('assignment_codes')
      .eq('id', childId)
      .maybeSingle();
    if (error || !data) return;

    const codes = data.assignment_codes ?? [];
    if (codes.includes(code)) return;

    await supabase
      .from('children')
      .update({ assignment_codes: [...codes, code] })
      .eq('id', childId);
  } catch (e) {
    // 紐づけに失敗しても音読自体は続けられるべきなので握りつぶす
    console.warn('課題の紐づけに失敗:', e);
  }
}

/** その子に紐づく課題のうち、期限内のものを新しい順で返す */
export async function fetchChildAssignments(childId) {
  if (!childId) return [];
  try {
    const { data: child } = await supabase
      .from('children')
      .select('assignment_codes')
      .eq('id', childId)
      .maybeSingle();

    const codes = child?.assignment_codes ?? [];
    if (codes.length === 0) return [];

    const { data, error } = await supabase
      .from('assignments')
      .select('id, title, body, expires_at')
      .in('id', codes)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    if (error) return [];
    return data ?? [];
  } catch {
    return [];
  }
}

// ── 先生用（ログイン必須） ─────────────────────────

const CODE_CHARS = 'abcdefghijkmnpqrstuvwxyz23456789'; // 紛らわしい文字（l, o, 0, 1）は除く

function generateCode() {
  return Array.from({ length: 6 }, () =>
    CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]
  ).join('');
}

/**
 * 課題を作成してコードを返す
 */
export async function createAssignment(teacherId, title, body, grade = 2, expiresInDays = 14) {
  const expiresAt = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000);
  const code = generateCode();
  const { error } = await supabase.from('assignments').insert({
    id: code,
    teacher_id: teacherId,
    title,
    body,
    grade,
    expires_at: expiresAt.toISOString(),
  });
  if (error) throw new Error(error.message);
  return code;
}

/**
 * 自分が作った課題の一覧（新しい順）
 */
export async function fetchMyAssignments(teacherId) {
  const { data, error } = await supabase
    .from('assignments')
    // ⚠️ 列名を列挙すると、未追加の列が1つあるだけでクエリ全体が失敗し、
    //    「課題が0件」に見えてしまう（実際にそれで事故った）。
    //    スキーマ変更に強くするため * を使う。
    .select('*')
    .eq('teacher_id', teacherId)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data ?? [];
}

/** 課題1件の全体（本文込み）を取得 */
export async function fetchAssignmentDetail(assignmentId) {
  const { data, error } = await supabase
    .from('assignments')
    // 列を列挙すると未追加の列でクエリ全体が落ちるため * を使う（上記と同じ理由）
    .select('*')
    .eq('id', assignmentId)
    .maybeSingle();
  if (error) return null;
  return data;
}

/**
 * 課題を更新する。
 * ⚠️ 生徒は開くたびに本文を取り直すので、編集は即座に反映される。
 *    ただし提出済みの点数は「編集前の本文」との比較結果なので、
 *    提出がある課題の本文を変えると点数が比較できなくなる。
 */
export async function updateAssignment(assignmentId, updates) {
  // 作成時は null のままにして、編集したときだけ入れる
  // （「編集済みかどうか」が値の有無だけで判断できる）
  const withStamp = { ...updates, updated_at: new Date().toISOString() };

  const { error } = await supabase
    .from('assignments')
    .update(withStamp)
    .eq('id', assignmentId);
  if (!error) return;

  // updated_at 列がまだ無い環境では、編集自体は通るようにフォールバックする
  if (/updated_at/.test(error.message)) {
    const { error: retryErr } = await supabase
      .from('assignments')
      .update(updates)
      .eq('id', assignmentId);
    if (retryErr) throw new Error(retryErr.message);
    return;
  }
  throw new Error(error.message);
}

/**
 * 課題を削除する。
 * ⚠️ submissions は ON DELETE CASCADE なので、生徒の提出記録も一緒に消える。
 *    呼ぶ前に必ず件数を見せて確認を取ること。
 *
 * ⚠️ ここで直接 delete しないこと。行だけ消すと Storage の録音が最大30日残り、
 *    「課題を消した＝音声も消えた」という先生の認識とズレる。
 *    録音を消すには service role が要るので、サーバー側でまとめて行う。
 */
export async function deleteAssignment(assignmentId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('ログインが必要です');

  const res = await fetch('/api/assignment-delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ code: assignmentId }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `削除に失敗しました（${res.status}）`);
  }
}

/**
 * その先生の今月の使用状況（使用率メーター用）。
 * 上限・単価はサーバーから受け取る（クライアントに定数をコピーしない）。
 */
export async function fetchUsage(teacherId) {
  try {
    const res = await fetch(`/api/assignment-usage?teacherId=${encodeURIComponent(teacherId)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * 指定した課題の提出一覧（RLSで作成者のみ取得可）
 */
export async function fetchSubmissions(assignmentId) {
  const { data, error } = await supabase
    .from('submissions')
    // 列を列挙すると未追加の列でクエリ全体が落ちるため * を使う（エラー履歴050）
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data ?? [];
}
